import {
  type AccessConfigMap,
  AccessTypeMap,
  type ApplicationRoleAccess,
  type EnterpriseRoleAccess,
  type EntitlementAccess,
} from '../types';

import { type TrackedListItem } from './AccessModal';

interface ApiResponseData {
  entitlement?: EntitlementAccess;
  enterpriseRole?: EnterpriseRoleAccess;
  applicationRole?: ApplicationRoleAccess;
}

/**
 * Transforms raw API response data into an array of TrackedListItems with
 * unified tracker metadata (dates, justification, selection state).
 *
 * @param data - The destructured entitlement, enterprise role, and application role responses
 * @param accessConfigMap - The typed access configuration map for date/justification rules
 * @param resolveIsChecked - Function to determine the isChecked state per item.
 *   Receives `(itemIsChecked: boolean | undefined)` from the raw API item.
 * @returns An array of TrackedListItems ready for use in listRows or activeRows.
 */
export default function transformApiResponseToTrackedItems(
  data: ApiResponseData,
  accessConfigMap: AccessConfigMap,
  resolveIsChecked: (itemIsChecked: boolean | undefined) => boolean
): TrackedListItem[] {
  const { entitlement, enterpriseRole, applicationRole } = data;

  const trackedItems: TrackedListItem[] = [
    ...(entitlement?.data.map((e) => {
      const entitlementConfig =
        accessConfigMap[AccessTypeMap.entitlement]?.[e.entitlementtype];

      const isStartDate = Boolean(entitlementConfig?.isStartDate);
      const isEndDate = Boolean(entitlementConfig?.isEndDate);
      const isStartDateMandatory = Boolean(
        entitlementConfig?.isStartDateMandatory
      );
      const isEndDateMandatory = Boolean(entitlementConfig?.isEndDateMandatory);
      const currentStartDate =
        e.startdate && e.startdate.trim().length > 0
          ? new Date(e.startdate)
          : new Date();
      let currentEndDate =
        e.enddate && e.enddate.trim().length > 0 ? new Date(e.enddate) : null;

      // Apply default time frame logic if conditions are met
      if (
        isStartDate &&
        isEndDate &&
        entitlementConfig &&
        entitlementConfig.defaultTimeFrameHrs !== null &&
        currentEndDate !== null &&
        (currentStartDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24) <
          Number(entitlementConfig.defaultTimeFrameHrs) / 24
      ) {
        const today = new Date();

        currentEndDate = new Date(
          today.getTime() +
            Number(entitlementConfig.defaultTimeFrameHrs) * 60 * 60 * 1000
        );
      }

      const isJustificationRequired = Boolean(
        e.mandatoryBusinessJustification === true &&
          (e.soxcritical === 'CRITICAL' || e.soxcritical === 'VERY_HIGH')
      );

      return {
        ...e,
        tracker: {
          id: e.entitlement_valuekey,
          type: AccessTypeMap.entitlement,
          isChecked: resolveIsChecked(e.isChecked),
          isStartDate,
          isEndDate,
          isStartDateMandatory,
          isEndDateMandatory,
          isStartDateCritical: false,
          isEndDateCritical: false,
          currentStartDate,
          currentEndDate,
          allowedEndDateRange: entitlementConfig?.maxTimeFrameHrs,
          isStartDateDisabled: e.isStartDateDisabled,
          isJustificationRequired,
          isJustificationCritical: false,
          currentJustification: e.justification ?? '',
        },
      };
    }) ?? []),
    ...(enterpriseRole?.data.map((e) => {
      const roleConfig = accessConfigMap[AccessTypeMap.enterpriseRole];
      const isStartDate = Boolean(roleConfig?.isStartDate);
      const isEndDate = Boolean(roleConfig?.isEndDate);
      const isStartDateMandatory = Boolean(roleConfig?.isStartDateMandatory);
      const isEndDateMandatory = Boolean(roleConfig?.isEndDateMandatory);
      const currentStartDate =
        e.startdate && e.startdate.trim().length > 0
          ? new Date(e.startdate)
          : new Date();
      const currentEndDate =
        e.enddate && e.enddate.trim().length > 0 ? new Date(e.enddate) : null;
      const isJustificationRequired = Boolean(
        e.mandatoryBusinessJustification === true &&
          (e.soxcritical === 'CRITICAL' || e.soxcritical === 'VERY_HIGH')
      );

      return {
        ...e,
        tracker: {
          id: e.roleid,
          type: AccessTypeMap.enterpriseRole,
          isChecked: resolveIsChecked(e.isChecked),
          isStartDate,
          isEndDate,
          isStartDateMandatory,
          isEndDateMandatory,
          isStartDateCritical: false,
          isEndDateCritical: false,
          currentStartDate,
          currentEndDate,
          isStartDateDisabled: e.isStartDateDisabled,
          isJustificationCritical: false,
          isJustificationRequired,
          currentJustification: e.justification ?? '',
        },
      };
    }) ?? []),
    ...(applicationRole?.data.map((e) => {
      const roleConfig = accessConfigMap[AccessTypeMap.applicationRole];
      const isStartDate = Boolean(roleConfig?.isStartDate);
      const isEndDate = Boolean(roleConfig?.isEndDate);
      const isStartDateMandatory = Boolean(roleConfig?.isStartDateMandatory);
      const isEndDateMandatory = Boolean(roleConfig?.isEndDateMandatory);
      const currentStartDate =
        e.startdate && e.startdate.trim().length > 0
          ? new Date(e.startdate)
          : new Date();
      const currentEndDate =
        e.enddate && e.enddate.trim().length > 0 ? new Date(e.enddate) : null;
      const isJustificationRequired = Boolean(
        e.mandatoryBusinessJustification === true &&
          (e.soxcritical === 'CRITICAL' || e.soxcritical === 'VERY_HIGH')
      );

      return {
        ...e,
        tracker: {
          id: e.roleid,
          type: AccessTypeMap.applicationRole,
          isChecked: resolveIsChecked(e.isChecked),
          isStartDate,
          isEndDate,
          isStartDateMandatory,
          isEndDateMandatory,
          isStartDateCritical: false,
          isEndDateCritical: false,
          currentStartDate,
          currentEndDate,
          isStartDateDisabled: e.isStartDateDisabled,
          isJustificationCritical: false,
          isJustificationRequired,
          currentJustification: e.justification ?? '',
        },
      };
    }) ?? []),
  ];

  return trackedItems;
}
